import * as React from 'react'

import { Search, X } from 'lucide-react'

import { cn } from '@/lib/utils'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '~/client/src/shared/components/shadcn-ui/input-group'
import Localization from '~/client/src/shared/localization/LocalizationManager'

interface IProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  debounceMs?: number
  autoFocus?: boolean
  className?: string
  testId?: string
  size?: 'default' | 'lg'
}

export default function SearchInput({
  value,
  onChange,
  placeholder,
  debounceMs = 300,
  autoFocus,
  className,
  testId,
  size,
}: IProps): React.ReactElement {
  const [internal, setInternal] = React.useState(value)

  const onChangeRef = React.useRef(onChange)
  onChangeRef.current = onChange

  // Mirror external resets (e.g. reset-all) back into the field.
  React.useEffect(() => {
    setInternal(value)
  }, [value])

  // Debounce upstream propagation so filtering doesn't run per keystroke.
  React.useEffect(() => {
    if (internal === value) return
    const id = setTimeout(() => onChangeRef.current(internal), debounceMs)
    return () => clearTimeout(id)
  }, [internal, value, debounceMs])

  const handleClear = () => {
    setInternal('')
    onChangeRef.current('')
  }

  return (
    <InputGroup size={size} className={cn('tw:w-56', className)}>
      <InputGroupAddon>
        <Search aria-hidden="true" />
      </InputGroupAddon>
      <InputGroupInput
        value={internal}
        onChange={event => setInternal(event.target.value)}
        placeholder={placeholder ?? Localization.translator.search}
        autoFocus={autoFocus}
        data-testid={testId}
      />
      {!!internal && (
        <InputGroupAddon align="inline-end">
          <InputGroupButton
            size="icon-xs"
            onClick={handleClear}
            aria-label={Localization.translator.clear}
          >
            <X aria-hidden="true" />
          </InputGroupButton>
        </InputGroupAddon>
      )}
    </InputGroup>
  )
}
